/**
 * Artificial delay and seeded failure injection.
 *
 * Read paths get 180ms to 500ms of jittered delay so loading states are real
 * rather than theoretical, which is what makes "the overview renders
 * meaningful content before metrics resolve" testable instead of claimed.
 */

export interface DelayOptions {
  min?: number;
  max?: number;
  /** Speed multiplier from the demo dock. "instant" resolves on a microtask. */
  speed?: number | 'instant';
}

export function delay({ min = 180, max = 500, speed = 1 }: DelayOptions = {}): Promise<void> {
  if (speed === 'instant') return Promise.resolve();
  const jittered = min + Math.random() * Math.max(0, max - min);
  return new Promise((resolve) => setTimeout(resolve, jittered / speed));
}

/** Deterministic 0 to 1 from a string, so an injected failure is repeatable. */
export function seededUnit(key: string): number {
  let hash = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 0) % 10_000) / 10_000;
}

export class MockApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'MockApiError';
  }
}

/** Fail a named read path at a fixed rate, repeatably. */
export function maybeFail(key: string, rate: number, message: string, code = 'mock_failure'): void {
  if (rate > 0 && seededUnit(key) < rate) throw new MockApiError(message, code);
}
